import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  ArrowUpRight, ArrowDownRight, Minus, BarChart3, Users, Loader2,
  Target, Flag, ClipboardList, Dumbbell, TrendingUp, FileDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const SEASONS = ['2025/2026', '2024/2025', '2023/2024', '2022/2023'];

// Presentational sub-components — declared at module scope to avoid remounting on every parent render.
const DeltaIndicator = ({ diff, suffix = '', invert = false }) => {
  if (diff === null || diff === undefined) return <Minus size={12} className="text-gray-400" />;
  const positive = invert ? diff < 0 : diff > 0;
  const negative = invert ? diff > 0 : diff < 0;
  const color = positive ? 'text-emerald-600' : negative ? 'text-red-500' : 'text-gray-400';
  const Icon = positive ? ArrowUpRight : negative ? ArrowDownRight : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${color}`}>
      <Icon size={13} />
      {diff > 0 ? '+' : ''}{diff}{suffix}
    </span>
  );
};

const MetricRow = ({ label, s1, s2, diff, suffix = '', invert = false }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <span className="text-sm text-gray-600 dark:text-gray-400 w-40">{label}</span>
    <span className="text-sm font-medium text-gray-500 dark:text-gray-400 w-20 text-center">{s1 ?? '-'}{suffix}</span>
    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 w-20 text-center">{s2 ?? '-'}{suffix}</span>
    <div className="w-20 text-right"><DeltaIndicator diff={diff} suffix={suffix} invert={invert} /></div>
  </div>
);

const SeasonReportCompare = () => {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [athletes, setAthletes] = useState([]);
  const [selectedAthlete, setSelectedAthlete] = useState('');
  const [season1, setSeason1] = useState('2024/2025');
  const [season2, setSeason2] = useState('2025/2026');
  const [compareData, setCompareData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAthletes, setLoadingAthletes] = useState(true);

  useEffect(() => {
    const fetchAthletes = async () => {
      try {
        const res = await fetch(`${API}/api/athletes`, { headers });
        if (res.ok) setAthletes(await res.json());
      } catch { /* silent */ }
      finally { setLoadingAthletes(false); }
    };
    fetchAthletes();
  }, []);

  const fetchComparison = useCallback(async () => {
    if (!selectedAthlete || !season1 || !season2) return;
    if (season1 === season2) { toast.error('Select two different seasons'); return; }
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/reports/season/compare/${selectedAthlete}?season1=${season1}&season2=${season2}`,
        { headers }
      );
      if (res.ok) {
        setCompareData(await res.json());
      } else {
        toast.error('Failed to load comparison');
      }
    } catch {
      toast.error('Error fetching comparison data');
    } finally {
      setLoading(false);
    }
  }, [selectedAthlete, season1, season2]);

  // DeltaIndicator and MetricRow are defined at module scope (above) to prevent remounting.

  const handleExport = () => {
    if (!compareData) return;
    const a = compareData.athlete;
    const d = compareData.deltas;
    let csv = `Season Comparison Report - ${a.full_name}\n`;
    csv += `Position: ${a.position || 'N/A'}, Squad: ${a.squad || 'N/A'}\n\n`;
    csv += `Metric,${season1},${season2},Change\n`;
    csv += `Matches,${d.total_matches?.season1 ?? 0},${d.total_matches?.season2 ?? 0},${d.total_matches?.diff ?? 0}\n`;
    csv += `Minutes,${d.total_minutes?.season1 ?? 0},${d.total_minutes?.season2 ?? 0},${d.total_minutes?.diff ?? 0}\n`;
    csv += `Goals,${d.goals?.season1 ?? 0},${d.goals?.season2 ?? 0},${d.goals?.diff ?? 0}\n`;
    csv += `Assists,${d.assists?.season1 ?? 0},${d.assists?.season2 ?? 0},${d.assists?.diff ?? 0}\n`;
    csv += `Avg Rating,${d.avg_rating?.season1 ?? 0},${d.avg_rating?.season2 ?? 0},${d.avg_rating?.diff ?? 0}\n`;
    csv += `Attendance %,${d.attendance_rate?.season1 ?? 0},${d.attendance_rate?.season2 ?? 0},${d.attendance_rate?.diff ?? 0}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${a.full_name.replace(/\s+/g, '_')}_season_comparison.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const d = compareData?.deltas || {};
  const s1Data = compareData?.season1 || {};
  const s2Data = compareData?.season2 || {};

  return (
    <Layout>
      <div className="space-y-5 max-w-5xl" data-testid="season-compare-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100" data-testid="page-title">
            <BarChart3 size={22} className="inline mr-2 mb-1 text-pfap-green" />
            Season Comparison
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Compare an athlete's performance across two seasons
          </p>
        </div>

        {/* Selectors */}
        <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="compare-selectors">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[200px]">
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Athlete</label>
                <Select value={selectedAthlete} onValueChange={setSelectedAthlete}>
                  <SelectTrigger className="h-9" data-testid="select-athlete">
                    <SelectValue placeholder={loadingAthletes ? 'Loading...' : 'Select Athlete'} />
                  </SelectTrigger>
                  <SelectContent>
                    {athletes.map(a => (
                      <SelectItem key={a.id} value={a.id}>{a.full_name} — {a.squad || 'N/A'}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Season 1 (older)</label>
                <Select value={season1} onValueChange={setSeason1}>
                  <SelectTrigger className="h-9" data-testid="select-season1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEASONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-36">
                <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Season 2 (newer)</label>
                <Select value={season2} onValueChange={setSeason2}>
                  <SelectTrigger className="h-9" data-testid="select-season2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SEASONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={fetchComparison} disabled={loading || !selectedAthlete}
                className="bg-pfap-green hover:bg-pfap-green/90 text-white h-9" data-testid="compare-btn">
                {loading ? <Loader2 size={14} className="animate-spin mr-1" /> : <BarChart3 size={14} className="mr-1" />}
                Compare
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        {compareData && (
          <>
            {/* Athlete Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-testid="compare-athlete-name">
                  {compareData.athlete.full_name}
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge className="text-[10px] bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{compareData.athlete.position}</Badge>
                  <span className="text-xs text-gray-400">{compareData.athlete.squad}</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={handleExport} className="h-8" data-testid="export-csv-btn">
                <FileDown size={14} className="mr-1.5" /> Export CSV
              </Button>
            </div>

            {/* Performance Metrics */}
            <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="performance-comparison">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                  <Dumbbell size={15} className="inline mr-1.5 mb-0.5" /> Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Column headers */}
                <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-600 mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-40">Metric</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-20 text-center">{season1}</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-20 text-center">{season2}</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-20 text-right">Change</span>
                </div>
                <MetricRow label="Matches Played" s1={d.total_matches?.season1} s2={d.total_matches?.season2} diff={d.total_matches?.diff} />
                <MetricRow label="Total Minutes" s1={d.total_minutes?.season1} s2={d.total_minutes?.season2} diff={d.total_minutes?.diff} />
                <MetricRow label="Goals" s1={d.goals?.season1} s2={d.goals?.season2} diff={d.goals?.diff} />
                <MetricRow label="Assists" s1={d.assists?.season1} s2={d.assists?.season2} diff={d.assists?.diff} />
                <MetricRow label="Avg Rating" s1={d.avg_rating?.season1} s2={d.avg_rating?.season2} diff={d.avg_rating?.diff} />
              </CardContent>
            </Card>

            {/* Attendance */}
            <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="attendance-comparison">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                  <ClipboardList size={15} className="inline mr-1.5 mb-0.5" /> Attendance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-600 mb-1">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-40">Metric</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-20 text-center">{season1}</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-20 text-center">{season2}</span>
                  <span className="text-[10px] uppercase tracking-wider text-gray-400 w-20 text-right">Change</span>
                </div>
                <MetricRow label="Attendance Rate" s1={d.attendance_rate?.season1} s2={d.attendance_rate?.season2} diff={d.attendance_rate?.diff} suffix="%" />
                <MetricRow label="Sessions Attended" s1={s1Data.attendance?.attended} s2={s2Data.attendance?.attended} diff={(s2Data.attendance?.attended || 0) - (s1Data.attendance?.attended || 0)} />
              </CardContent>
            </Card>

            {/* PDP & Flags side by side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="pdp-comparison">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                    <Target size={15} className="inline mr-1.5 mb-0.5" /> PDP Cycles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase text-gray-400 mb-1">{season1}</p>
                      {(s1Data.pdp || []).length === 0 ? (
                        <p className="text-xs text-gray-400">No PDP data</p>
                      ) : (s1Data.pdp || []).map((c, i) => (
                        <div key={i} className="flex items-center gap-1.5 mb-1">
                          <Badge className="text-[9px] bg-gray-100 dark:bg-gray-700 text-gray-500">{c.status || 'N/A'}</Badge>
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{c.title}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-gray-400 mb-1">{season2}</p>
                      {(s2Data.pdp || []).length === 0 ? (
                        <p className="text-xs text-gray-400">No PDP data</p>
                      ) : (s2Data.pdp || []).map((c, i) => (
                        <div key={i} className="flex items-center gap-1.5 mb-1">
                          <Badge className="text-[9px] bg-gray-100 dark:bg-gray-700 text-gray-500">{c.status || 'N/A'}</Badge>
                          <span className="text-xs text-gray-700 dark:text-gray-300 truncate">{c.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-testid="flags-comparison">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-gray-700 dark:text-gray-300">
                    <Flag size={15} className="inline mr-1.5 mb-0.5" /> Flags & Alerts
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] uppercase text-gray-400 mb-1">{season1}</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{(s1Data.flags || []).length}</p>
                      <p className="text-[10px] text-gray-400">flag{(s1Data.flags || []).length !== 1 ? 's' : ''} raised</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-gray-400 mb-1">{season2}</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{(s2Data.flags || []).length}</p>
                      <p className="text-[10px] text-gray-400">flag{(s2Data.flags || []).length !== 1 ? 's' : ''} raised</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Data source info */}
            <div className="flex items-center gap-4 text-[10px] text-gray-400 dark:text-gray-500">
              <span>{season1}: {s1Data.source === 'snapshot' ? 'Saved Report' : 'Live Data'}</span>
              <span>{season2}: {s2Data.source === 'snapshot' ? 'Saved Report' : 'Live Data'}</span>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default SeasonReportCompare;
